import { useState } from "react";
import dayjs from "dayjs";
import { PrimaryButton, TextInput } from "@normasteaching/norma-ui";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useUpsertWithdrawalRequest } from "../../SpeakingClassService/Withdrawal/useUpsertWithdrawalRequest.ts";
import {
  useWithdrawalRequestsByEmail,
  WithdrawalRequestsByEmail,
} from "../../SpeakingClassService/Withdrawal/useWithdrawalRequestsByEmail.ts";
import { WITHDRAWAL_PLAN_TYPE_LABELS } from "./withdrawalLabels.ts";
import { WithdrawalWindowNotice } from "./WithdrawalWindowNotice.tsx";

type Props = {
  onClose: () => void;
  onCreated: () => void;
};

export function CreateWithdrawalRequestModal({ onClose, onCreated }: Props) {
  const [email, setEmail] = useState("");
  const [requestedAt, setRequestedAt] = useState(dayjs().format("YYYY-MM-DD"));
  const [notes, setNotes] = useState("");
  const [purchaseKey, setPurchaseKey] = useState<string | null>(null);
  const [lookup, setLookup] = useState<WithdrawalRequestsByEmail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { getByEmail, loading: loadingLookup } = useWithdrawalRequestsByEmail();
  const { createRequest, loading: creating } = useUpsertWithdrawalRequest();

  const selectedPurchase = lookup?.withdrawablePurchases.find(
    (purchase) => (purchase.purchaseKey ?? null) === purchaseKey,
  );

  const handleLookup = async () => {
    setError(null);
    setLookup(null);
    setPurchaseKey(null);
    if (!email) {
      setError("Inserisci l'email del cliente");
      return;
    }
    const result = await getByEmail(email.trim());
    if (!result) {
      setError("Nessun utente trovato in piattaforma con questa email");
      return;
    }
    setLookup(result);
    if (result.withdrawablePurchases.length === 1) {
      setPurchaseKey(result.withdrawablePurchases[0].purchaseKey ?? null);
    }
  };

  const handleConfirm = async () => {
    setError(null);
    if (!lookup) {
      setError("Cerca prima il cliente");
      return;
    }
    if (!purchaseKey && lookup.withdrawablePurchases.length > 1) {
      setError("Seleziona l'acquisto a cui si riferisce la richiesta");
      return;
    }
    try {
      await createRequest({
        userEmail: email.trim(),
        purchaseKey: purchaseKey ?? undefined,
        requestedAtUtc: new Date(requestedAt),
        notes: notes || undefined,
      });
      onCreated();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(
        err.response?.data?.message ??
          "Errore durante la creazione della richiesta. Riprova.",
      );
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Nuova richiesta di recesso">
      <div className="space-y-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            Email cliente
          </label>
          <div className="flex gap-2">
            <TextInput
              type="email"
              placeholder="cliente@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              onClick={handleLookup}
              disabled={loadingLookup}
              className="px-3 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
            >
              {loadingLookup ? "Cerco..." : "Cerca"}
            </button>
          </div>
        </div>

        {lookup && lookup.withdrawablePurchases.length === 0 && (
          <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg p-3">
            Questo utente non ha acquisti che danno diritto al recesso.
          </p>
        )}

        {lookup && lookup.withdrawablePurchases.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Acquisto
            </label>
            {lookup.withdrawablePurchases.map((purchase) => (
              <label
                key={purchase.purchaseKey ?? purchase.purchasedAtUtc}
                className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                  purchaseKey === purchase.purchaseKey
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200"
                }`}
              >
                <input
                  type="radio"
                  name="purchaseKey"
                  className="mt-1 w-4 h-4"
                  checked={purchaseKey === purchase.purchaseKey}
                  onChange={() => setPurchaseKey(purchase.purchaseKey ?? null)}
                />
                <div className="flex-1 text-sm">
                  <div className="font-medium text-gray-900">
                    {purchase.plans
                      .map((plan) => WITHDRAWAL_PLAN_TYPE_LABELS[plan.planType])
                      .join(" + ")}{" "}
                    — acquisto del{" "}
                    {dayjs(purchase.purchasedAtUtc).format("DD/MM/YYYY")}
                  </div>
                  <div className="text-gray-500 text-xs">
                    Recesso entro il{" "}
                    {dayjs(purchase.withdrawalDeadlineUtc).format("DD/MM/YYYY")}
                    {!purchase.isWithinWithdrawalWindow && " (termine scaduto)"}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        {lookup && lookup.requests.length > 0 && (
          <p className="text-sm text-gray-600">
            Questo utente ha già {lookup.requests.length} richiesta/e
            registrata/e.
          </p>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">
            Data della richiesta
          </label>
          <TextInput
            type="date"
            value={requestedAt}
            onChange={(e) => setRequestedAt(e.target.value)}
          />
        </div>

        {selectedPurchase && requestedAt && (
          <WithdrawalWindowNotice
            requestedAtUtc={new Date(requestedAt)}
            withdrawalDeadlineUtc={selectedPurchase.withdrawalDeadlineUtc}
          />
        )}

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700">Note</label>
          <TextInput
            placeholder="Es. richiesta arrivata via email il ..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
          >
            Annulla
          </button>
          <PrimaryButton
            label={creating ? "Salvataggio..." : "Crea richiesta"}
            onClick={handleConfirm}
            disabled={creating || !lookup}
          />
        </div>
      </div>
    </Modal>
  );
}
